import { useEffect, useState } from 'react';

import { TextField } from '@mui/material';

import { nonBlank } from '@karehero/helpers/string';

import { Button } from '@/components/atoms/Button/Button';
import { BottomSheetDialog } from '@/pwa/atoms/BottomSheetDialog/BottomSheetDialog';

const SAVE_LABEL = 'Save';

/** A blank line between paragraphs, as they are read back. */
const PARAGRAPH_BREAK = '\n\n';

export interface CardEditSheetProps {
  isOpen: boolean;
  /** The card's own title. */
  title: string;
  lines: string[];
  onClose: () => void;
  /** Called with the corrected lines, one per paragraph. */
  onSave: (lines: string[]) => void;
}

/** The text split back into lines on its blank lines, empty ones dropped. */
export const linesOfText = (text: string) => nonBlank(text.split(/\n\s*\n/));

/**
 * CardEditSheet corrects a card's lines in place: one text field with a
 * paragraph for each line, saved back as lines.
 */
export const CardEditSheet = ({
  isOpen,
  title,
  lines,
  onClose,
  onSave,
}: CardEditSheetProps) => {
  const [text, setText] = useState(lines.join(PARAGRAPH_BREAK));

  // Opened afresh each time, from the lines as they are now.
  useEffect(() => {
    if (isOpen) setText(lines.join(PARAGRAPH_BREAK));
  }, [isOpen, lines]);

  const saved = linesOfText(text);

  return (
    <BottomSheetDialog
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      footer={
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          isDisabled={saved.length === 0}
          ariaLabel={SAVE_LABEL}
          onPress={() => onSave(saved)}
        >
          {SAVE_LABEL}
        </Button>
      }
    >
      <TextField
        multiline
        fullWidth
        minRows={6}
        value={text}
        onChange={(e) => setText(e.target.value)}
        slotProps={{ htmlInput: { 'aria-label': title } }}
      />
    </BottomSheetDialog>
  );
};

CardEditSheet.displayName = 'CardEditSheet';
export default CardEditSheet;
